import { DateTime } from 'luxon'

import { UserCreateDTO } from '@/core/api/dtos/request/users/create'
import { UserResponseDTO } from '@/core/api/dtos/response/users/user'
import { User } from '@/core/api/entities/user'
import { AccountMapper } from '@/core/api/mappers/account/accountMapper'
import { GenericMapper } from '@/core/api/mappers/genericMapper'
import { RoleMapper } from '@/core/api/mappers/role/roleMapper'
import { ResellerSettingsMapper } from '@/core/api/mappers/user/resellerSettingsMapper'

export class UserMapper {
	static toEntity = (dto: UserResponseDTO): User => {
		const dates = GenericMapper.mapDates({
			createdAt: dto.created_at,
			updatedAt: dto.updated_at,
		})

		const account = dto.account
			? AccountMapper.toEntity(dto.account.data)
			: undefined

		const childrenAccounts = dto.children_accounts
			? AccountMapper.toEntities(dto.children_accounts.data)
			: undefined

		const roles = dto.roles
			? { data: RoleMapper.toEntities(dto.roles.data) }
			: undefined

		const resellerSettings = dto.reseller_settings
			? ResellerSettingsMapper.toEntity(dto.reseller_settings)
			: undefined

		return User.create({
			id: dto.id,
			accountId: dto.account_id,
			uuid: dto.uuid,
			email: dto.email,
			name: dto.name,
			firstname: dto.firstname ?? undefined,
			lastname: dto.lastname ?? undefined,
			phoneNumber: dto.phone_number,
			roleId: dto.role_id ?? dto.roleId ?? 0, // Use role_id from API, fallback to roleId for backward compatibility
			country: dto.country,
			status: dto.status,
			createdAt: dates.createdAt,
			updatedAt: dates.updatedAt,
			disabledAt: dto.disabled_at
				? DateTime.fromISO(dto.disabled_at)
				: undefined,
			disabled: dto.disabled,
			suspended: dto.suspended,
			test: dto.test,
			avatar: dto.avatar,
			lastSeen: dto.lastSeen ? DateTime.fromISO(dto.lastSeen) : undefined,
			comments: dto.comments,
			permissions: dto.permissions,
			isResellerSubaccount: dto.is_reseller_subaccount,
			posthogId: dto.posthog_id,
			resellerSettings,
			account,
			childrenAccounts,
			roles,
		})
	}

	static toEntities = (dtos: UserResponseDTO[]): User[] => {
		return dtos.map((dto) => UserMapper.toEntity(dto))
	}

	static toCreateDTO = (entity: User): UserCreateDTO => ({
		email: entity.email,
		first_name: entity.firstname,
		last_name: entity.lastname,
		role: entity.role,
	})
}
